/**
 * Pure-logic backing for ParametersFormEditor: the parameter-type schema,
 * value coercion / validation, JSON ↔ row conversion, and small utilities.
 *
 * Kept React-free so it can be unit-tested in isolation and reused if another
 * surface needs the same model.
 */

export const PARAMETER_TYPES = ['String', 'Int', 'Float', 'Bool', 'Array', 'Object'] as const;
export type ParameterType = (typeof PARAMETER_TYPES)[number];

export interface Row {
  /** Stable id so React keys don't shift while the user edits the name. */
  id: string;
  name: string;
  type: ParameterType;
  /** String representation the user is editing; coerced on serialize. */
  rawValue: string;
  description: string;
}

interface TypeMeta {
  label: string;
  /** Coerce a parsed JSON value into the editor's raw string. */
  toRaw: (value: unknown) => string;
  /** Coerce the editor's raw string back to a JSON value. */
  fromRaw: (raw: string) => unknown;
  /** Return a human-readable error when the raw string is invalid. */
  validate?: (raw: string) => string | undefined;
}

const FLOAT_LITERAL = /^-?\d+(\.\d+)?([eE][-+]?\d+)?$/;

const stringifyJson = (v: unknown): string => {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
};

const parseJson = (raw: string): { ok: true; value: unknown } | { ok: false } => {
  try {
    return { ok: true, value: JSON.parse(raw) };
  } catch {
    return { ok: false };
  }
};

const META: Record<ParameterType, TypeMeta> = {
  String: {
    label: 'String',
    toRaw: (v) => (v == null ? '' : typeof v === 'string' ? v : stringifyJson(v)),
    fromRaw: (r) => r,
  },
  Int: {
    label: 'Integer',
    toRaw: (v) => (v == null ? '' : String(v)),
    fromRaw: (r) => {
      const n = Number(r);
      return Number.isInteger(n) && r.trim() === String(n) ? n : r;
    },
    validate: (r) => (r.trim() && Number.isNaN(Number(r))
      ? 'Value must be a number — saved as a string until corrected.'
      : undefined),
  },
  Float: {
    label: 'Float',
    toRaw: (v) => (v == null ? '' : String(v)),
    fromRaw: (r) => {
      const n = Number(r);
      return !Number.isNaN(n) && FLOAT_LITERAL.test(r.trim()) ? n : r;
    },
    validate: (r) => (r.trim() && Number.isNaN(Number(r))
      ? 'Value must be a number — saved as a string until corrected.'
      : undefined),
  },
  Bool: {
    label: 'Boolean',
    // Preserve non-literal strings (e.g. `@appsetting('Foo')`) so expressions
    // round-trip through the editor; otherwise canonicalise to true/false.
    toRaw: (v) => (v === true ? 'true' : v === false || v == null ? 'false' : typeof v === 'string' ? v : 'false'),
    fromRaw: (r) => (r === 'true' ? true : r === 'false' || r === '' ? false : r),
  },
  Array: {
    label: 'Array',
    toRaw: (v) => (v == null ? '' : typeof v === 'string' ? v : stringifyJson(v)),
    fromRaw: (r) => {
      const parsed = parseJson(r);
      return parsed.ok ? parsed.value : r;
    },
    validate: (r) => {
      if (!r.trim()) return undefined;
      const parsed = parseJson(r);
      if (!parsed.ok) return 'Invalid JSON — saved as a raw string until corrected.';
      return Array.isArray(parsed.value) ? undefined : 'Value must be a JSON array.';
    },
  },
  Object: {
    label: 'Object',
    toRaw: (v) => (v == null ? '' : typeof v === 'string' ? v : stringifyJson(v)),
    fromRaw: (r) => {
      const parsed = parseJson(r);
      return parsed.ok ? parsed.value : r;
    },
    validate: (r) => {
      if (!r.trim()) return undefined;
      const parsed = parseJson(r);
      if (!parsed.ok) return 'Invalid JSON — saved as a raw string until corrected.';
      const v = parsed.value;
      return v && typeof v === 'object' && !Array.isArray(v)
        ? undefined
        : 'Value must be a JSON object.';
    },
  },
};

const TYPE_ALIASES: Record<string, ParameterType> = {
  string: 'String',
  int: 'Int',
  integer: 'Int',
  float: 'Float',
  number: 'Float',
  bool: 'Bool',
  boolean: 'Bool',
  array: 'Array',
  object: 'Object',
};

export const normalizeType = (raw: unknown): ParameterType =>
  TYPE_ALIASES[String(raw ?? '').toLowerCase()] ?? 'String';

export const labelOf = (type: ParameterType): string => META[type].label;
export const toRaw = (type: ParameterType, value: unknown): string => META[type].toRaw(value);
export const fromRaw = (type: ParameterType, raw: string): unknown => META[type].fromRaw(raw);
export const validateValue = (type: ParameterType, raw: string): string | undefined =>
  META[type].validate?.(raw);

let rowIdSeq = 0;
export const makeRowId = (): string => {
  rowIdSeq += 1;
  return `row-${rowIdSeq}`;
};

export const nextDefaultName = (existing: Iterable<string>): string => {
  const taken = new Set<string>();
  for (const n of existing) taken.add(n.trim());
  let i = 1;
  while (taken.has(`param${i}`)) i += 1;
  return `param${i}`;
};

export const findDuplicateNames = (rows: readonly Row[]): Set<string> => {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const n = r.name.trim();
    if (n) counts.set(n, (counts.get(n) ?? 0) + 1);
  }
  const dupes = new Set<string>();
  for (const [n, c] of counts) if (c > 1) dupes.add(n);
  return dupes;
};

/**
 * First user-visible structural error in `rows`, or `null` if the rows
 * round-trip losslessly through `rowsToJson`. Used to block Save — a JSON
 * object can't represent duplicate keys, so emitting that payload would
 * silently drop the earlier entry.
 */
export const validateRows = (rows: readonly Row[]): string | null => {
  if (rows.some((r) => !r.name.trim())) return 'One or more parameters are missing a name.';
  const dupes = findDuplicateNames(rows);
  if (dupes.size === 0) return null;
  const list = [...dupes].map((n) => `"${n}"`).join(', ');
  return `Duplicate parameter name${dupes.size > 1 ? 's' : ''}: ${list}.`;
};

export interface RowsParseResult {
  ok: boolean;
  rows: Row[];
}

export const rowsFromJson = (json: string): RowsParseResult => {
  const parsed = parseJson(json || '{}');
  if (!parsed.ok) return { ok: false, rows: [] };
  const root = parsed.value;
  if (!root || typeof root !== 'object' || Array.isArray(root)) {
    return { ok: false, rows: [] };
  }
  const rows: Row[] = Object.entries(root as Record<string, unknown>).map(([name, raw]) => {
    const obj = (raw && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {}) as { type?: unknown; value?: unknown; description?: unknown; metadata?: { description?: unknown } };
    const type = normalizeType(obj.type);
    const description =
      typeof obj.description === 'string'
        ? obj.description
        : typeof obj.metadata?.description === 'string'
          ? obj.metadata.description
          : '';
    return { id: makeRowId(), name, type, rawValue: toRaw(type, obj.value), description };
  });
  return { ok: true, rows };
};

export const rowsToJson = (rows: readonly Row[]): string => {
  const out: Record<string, unknown> = {};
  for (const row of rows) {
    const name = row.name.trim();
    if (!name) continue;
    const entry: Record<string, unknown> = {
      type: row.type,
      value: fromRaw(row.type, row.rawValue),
    };
    if (row.description) entry.description = row.description;
    // Last write wins on duplicate names — mirrors JSON object semantics.
    out[name] = entry;
  }
  return JSON.stringify(out, null, 2);
};

/** Immutable Set helpers — keep setState updaters one-liners. */
export const withAdded = <T>(set: ReadonlySet<T>, value: T): Set<T> => new Set(set).add(value);
export const withRemoved = <T>(set: ReadonlySet<T>, value: T): Set<T> => {
  if (!set.has(value)) return set as Set<T>;
  const next = new Set(set);
  next.delete(value);
  return next;
};
export const withToggled = <T>(set: ReadonlySet<T>, value: T): Set<T> =>
  set.has(value) ? withRemoved(set, value) : withAdded(set, value);
